import { InvestorDetailsLegalEntityAssetClassTargetsQuery } from '@/__generated__/graphql';
import { GroupedIcon } from '@/assets/icons/info-icons/groupedIcon';
import { MinusIcon } from '@/assets/icons/info-icons/minusIcon';
import { PlusIcon } from '@/assets/icons/info-icons/plusIcon';
import { InfoCircleIcon } from '@/assets/static/icons/info-circle';
import { Tooltip } from '@/components/basicComponents/tooltip';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { TableBodyWrapper, TableRowCell, TableRowWrapper, TableWrapper, TitleCell } from '@/components/table/styles';
import { currencyFormatter, formatCash } from '@/shared/utils/currency';
import { themeVar } from '@/styles/theme';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';
import {
  overCommitmentRatioModeSummaryTableColumns,
  pacingModelModeSimplifiedSummaryTableColumns,
  pacingModelModeSummaryTableColumns
} from '../../constants';
import SummaryTableCapacityCell from './cell/capacityCell';

const economicExposureToolTipContent = (
  row:
    | InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]
    | InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number]['subAssetClasses'][number]
) => {
  return (
    <TableBodyWrapper>
      <TableRowCell width={100}>
        <TextCell>Current NAV: {currencyFormatter.format(row.currentNAV)}</TextCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TextCell>Unfunded Commitment: {currencyFormatter.format(row.unfundedCommitments)}</TextCell>
      </TableRowCell>
      <TableRowCell width={100}>
        <TextCell>Economic Exposure: {currencyFormatter.format(row.economicExposure)}</TextCell>
      </TableRowCell>
    </TableBodyWrapper>
  );
};

interface SummaryTableRowProps {
  row: InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets'][number];
  ignoreAllocationTargets: boolean;
  targetSubAssetClasses: boolean;
  commitmentPlansTotal: number | null;
  targetOCRatio: number | null;
  entityType: InvestorDetailsEntityTypeEnum;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}

const SummaryTableRow = ({
  row,
  ignoreAllocationTargets,
  targetSubAssetClasses,
  commitmentPlansTotal,
  targetOCRatio,
  entityType,
  isExpanded,
  onToggleExpanded
}: SummaryTableRowProps) => {
  // if (ignoreAllocationTargets && targetOCRatio !== null) {
  //   return (
  //     <>
  //       <TableRowWrapper>
  //         <TableRowCell width={overCommitmentRatioModeSimplifiedSummaryTableColumns[0].width}>
  //           <AssetClassCell>
  //             {targetSubAssetClasses && row.subAssetClasses && (
  //               <PlusIconWrapper onClick={() => setShowSubAssetClass(!showSubAssetClass)}>
  //                 {showSubAssetClass ? <MinusIcon /> : <PlusIcon />}
  //               </PlusIconWrapper>
  //             )}
  //             <TextCell>{row.name}</TextCell>
  //           </AssetClassCell>
  //         </TableRowCell>
  //         <TableRowCell width={overCommitmentRatioModeSimplifiedSummaryTableColumns[1].width}>
  //           <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
  //             <FormattingTooltip >{row.economicExposure}</FormattingTooltip>
  //           </TextCell>
  //         </TableRowCell>
  //       </TableRowWrapper>
  //       <AnimatePresence>
  //         {targetSubAssetClasses && row.subAssetClasses && showSubAssetClass && (
  //           <motion.div
  //             key={`${row.id}-sub-asset-classes`}
  //             initial={{ height: 0, opacity: 0 }}
  //             animate={{ height: 'auto', opacity: 1 }}
  //             exit={{ height: 0, opacity: 0 }}
  //           >
  //             <TableWrapper padding="0">
  //               {row.subAssetClasses.map((subAssetClass) => (
  //                 <TableRowWrapper key={subAssetClass.id}>
  //                   <TableRowCell width={overCommitmentRatioModeSimplifiedSummaryTableColumns[0].width}>
  //                     <AssetClassCell>
  //                       <GroupIconWrapper>
  //                         <GroupedIcon width={26} height={26} />
  //                       </GroupIconWrapper>
  //                       <TextCell>{subAssetClass.name}</TextCell>
  //                     </AssetClassCell>
  //                   </TableRowCell>
  //                   <TableRowCell width={overCommitmentRatioModeSimplifiedSummaryTableColumns[1].width}>
  //                     <TextCell>
  //                       <FormattingTooltip >{subAssetClass.economicExposure}</FormattingTooltip>
  //                     </TextCell>
  //                   </TableRowCell>
  //                 </TableRowWrapper>
  //               ))}
  //             </TableWrapper>
  //           </motion.div>
  //         )}
  //       </AnimatePresence>
  //     </>
  //   );
  // }

  if (ignoreAllocationTargets || entityType === InvestorDetailsEntityTypeEnum.GroupedEntity) {
    return (
      <>
        <TableRowWrapper>
          <TableRowCell width={pacingModelModeSimplifiedSummaryTableColumns[0].width}>
            <AssetClassCell>
              {targetSubAssetClasses && row.subAssetClasses && (
                <PlusIconWrapper onClick={onToggleExpanded}>{isExpanded ? <MinusIcon /> : <PlusIcon />}</PlusIconWrapper>
              )}
              <TextCell>{row.name}</TextCell>
            </AssetClassCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSimplifiedSummaryTableColumns[1].width}>
            <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
              <FormattingTooltip>{row.currentNAV}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSimplifiedSummaryTableColumns[2].width}>
            <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
              <FormattingTooltip>{row.unfundedCommitments}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
        </TableRowWrapper>
        <AnimatePresence>
          {targetSubAssetClasses && row.subAssetClasses && isExpanded && (
            <motion.div
              key={`${row.id}-sub-asset-classes`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <TableWrapper padding="0">
                {row.subAssetClasses.map((subAssetClass) => (
                  <TableRowWrapper key={subAssetClass.id}>
                    <TableRowCell width={pacingModelModeSimplifiedSummaryTableColumns[0].width}>
                      <AssetClassCell>
                        <GroupIconWrapper>
                          <GroupedIcon width={26} height={26} />
                        </GroupIconWrapper>
                        <TextCell>{subAssetClass.name}</TextCell>
                      </AssetClassCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSimplifiedSummaryTableColumns[1].width}>
                      <TextCell>
                        <FormattingTooltip>{subAssetClass.currentNAV}</FormattingTooltip>
                      </TextCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSimplifiedSummaryTableColumns[2].width}>
                      <TextCell>
                        <FormattingTooltip>{subAssetClass.unfundedCommitments}</FormattingTooltip>
                      </TextCell>
                    </TableRowCell>
                  </TableRowWrapper>
                ))}
              </TableWrapper>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  if (targetOCRatio === null) {
    return (
      <>
        <TableRowWrapper>
          <TableRowCell width={pacingModelModeSummaryTableColumns[0].width}>
            <AssetClassCell>
              {targetSubAssetClasses && row.subAssetClasses && (
                <PlusIconWrapper onClick={onToggleExpanded}>{isExpanded ? <MinusIcon /> : <PlusIcon />}</PlusIconWrapper>
              )}
              <TextCell>{row.name}</TextCell>
            </AssetClassCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSummaryTableColumns[1].width}>
            <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
              {row.percentage === null ? '--' : <Tooltip title={`${row.percentage.toFixed(2)}%`}>{row.percentage.toFixed(2)}%</Tooltip>}
            </TextCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSummaryTableColumns[2].width}>
            <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
              <FormattingTooltip>{row.targetAllocation}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSummaryTableColumns[3].width}>
            <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
              <FormattingTooltip>{row.currentNAV}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSummaryTableColumns[4].width}>
            <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
              <FormattingTooltip>{row.unfundedCommitments}</FormattingTooltip>
            </TextCell>
          </TableRowCell>
          <TableRowCell width={pacingModelModeSummaryTableColumns[5].width}>
            <SummaryTableCapacityCell
              row={row}
              bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}
              commitmentPlansTotal={commitmentPlansTotal}
              targetOCRatio={targetOCRatio}
              targetSubAssetClasses={targetSubAssetClasses}
            />
          </TableRowCell>
        </TableRowWrapper>
        <AnimatePresence>
          {targetSubAssetClasses && row.subAssetClasses && isExpanded && (
            <motion.div
              key={`${row.id}-sub-asset-classes`}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              <TableWrapper padding="0">
                {row.subAssetClasses.map((subAssetClass) => (
                  <TableRowWrapper key={subAssetClass.id}>
                    <TableRowCell width={pacingModelModeSummaryTableColumns[0].width}>
                      <AssetClassCell>
                        <GroupIconWrapper>
                          <GroupedIcon width={26} height={26} />
                        </GroupIconWrapper>
                        <TextCell>{subAssetClass.name}</TextCell>
                      </AssetClassCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSummaryTableColumns[1].width}>
                      <TextCell>
                        {subAssetClass.percentage === null ? (
                          '--'
                        ) : (
                          <Tooltip title={`${subAssetClass.percentage.toFixed(2)}%`}>{subAssetClass.percentage.toFixed(2)}%</Tooltip>
                        )}
                      </TextCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSummaryTableColumns[2].width}>
                      <TextCell>
                        <FormattingTooltip>{subAssetClass.targetAllocation}</FormattingTooltip>
                      </TextCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSummaryTableColumns[3].width}>
                      <TextCell>
                        <FormattingTooltip>{subAssetClass.currentNAV}</FormattingTooltip>
                      </TextCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSummaryTableColumns[4].width}>
                      <TextCell>
                        <FormattingTooltip>{subAssetClass.unfundedCommitments}</FormattingTooltip>
                      </TextCell>
                    </TableRowCell>
                    <TableRowCell width={pacingModelModeSummaryTableColumns[5].width}>
                      <SummaryTableCapacityCell
                        row={subAssetClass}
                        commitmentPlansTotal={commitmentPlansTotal}
                        targetOCRatio={targetOCRatio}
                        targetSubAssetClasses={targetSubAssetClasses}
                      />
                    </TableRowCell>
                  </TableRowWrapper>
                ))}
              </TableWrapper>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <>
      <TableRowWrapper>
        <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[0].width}>
          <AssetClassCell>
            {targetSubAssetClasses && row.subAssetClasses && (
              <PlusIconWrapper onClick={onToggleExpanded}>{isExpanded ? <MinusIcon /> : <PlusIcon />}</PlusIconWrapper>
            )}
            <TextCell>{row.name}</TextCell>
          </AssetClassCell>
        </TableRowCell>
        <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[1].width}>
          <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
            {row.percentage === null ? '--' : <Tooltip title={`${row.percentage.toFixed(2)}%`}>{row.percentage.toFixed(2)}%</Tooltip>}
          </TextCell>
        </TableRowCell>
        <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[2].width}>
          <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
            <FormattingTooltip>{row.targetAllocation}</FormattingTooltip>
          </TextCell>
        </TableRowCell>
        <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[3].width}>
          <TextCell bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}>
            <CellWithTooltip>
              {formatCash(row.economicExposure)}
              <TooltipWrapper id="infoWrap">
                <Tooltip title={economicExposureToolTipContent(row)}>
                  <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                </Tooltip>
              </TooltipWrapper>
            </CellWithTooltip>
          </TextCell>
        </TableRowCell>
        <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[4].width}>
          <SummaryTableCapacityCell
            row={row}
            bold={Boolean(targetSubAssetClasses && row.subAssetClasses)}
            commitmentPlansTotal={commitmentPlansTotal}
            targetOCRatio={targetOCRatio}
          />
        </TableRowCell>
      </TableRowWrapper>
      <AnimatePresence>
        {targetSubAssetClasses && row.subAssetClasses && isExpanded && (
          <motion.div
            key={`${row.id}-sub-asset-classes`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <TableWrapper padding="0">
              {row.subAssetClasses.map((subAssetClass) => (
                <TableRowWrapper key={subAssetClass.id}>
                  <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[0].width}>
                    <AssetClassCell>
                      <GroupIconWrapper>
                        <GroupedIcon width={26} height={26} />
                      </GroupIconWrapper>
                      <TextCell>{subAssetClass.name}</TextCell>
                    </AssetClassCell>
                  </TableRowCell>
                  <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[1].width}>
                    <TextCell>
                      {subAssetClass.percentage === null ? (
                        '--'
                      ) : (
                        <Tooltip title={`${subAssetClass.percentage.toFixed(2)}%`}>{subAssetClass.percentage.toFixed(2)}%</Tooltip>
                      )}
                    </TextCell>
                  </TableRowCell>
                  <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[2].width}>
                    <TextCell>
                      <FormattingTooltip>{subAssetClass.targetAllocation}</FormattingTooltip>
                    </TextCell>
                  </TableRowCell>
                  <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[3].width}>
                    <TextCell>
                      <CellWithTooltip>
                        {formatCash(subAssetClass.economicExposure)}
                        <TooltipWrapper id="infoWrap">
                          <Tooltip title={economicExposureToolTipContent(subAssetClass)}>
                            <InfoCircleIcon fill={themeVar('font.disabled')} style={{ position: 'relative', top: '3px' }} />
                          </Tooltip>
                        </TooltipWrapper>
                      </CellWithTooltip>
                    </TextCell>
                  </TableRowCell>
                  <TableRowCell width={overCommitmentRatioModeSummaryTableColumns[4].width}>
                    <SummaryTableCapacityCell row={subAssetClass} commitmentPlansTotal={commitmentPlansTotal} targetOCRatio={targetOCRatio} />
                  </TableRowCell>
                </TableRowWrapper>
              ))}
            </TableWrapper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SummaryTableRow;

const AssetClassCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const PlusIconWrapper = styled.div`
  cursor: pointer;
  border-radius: 12px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    path {
      fill: ${themeVar('font.base')};
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.action.hover};
    transition: background-color 100ms ease;

    svg {
      path {
        fill: ${themeVar('context.light')};
      }
    }
  }
`;

const GroupIconWrapper = styled.div`
  padding: 5px;
  position: relative;
  top: -9px;
  margin-right: 14px;
  margin-left: 5px;
`;

const TextCell = styled(TitleCell)<{ bold?: boolean }>`
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
`;

const CellWithTooltip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TooltipWrapper = styled.div`
  opacity: 0;
`;
